import clsx from 'clsx'
import { useState } from 'react'

import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg'
import { Button } from 'components/common/button/Button'
import { Flex } from 'components/common/flex/Flex'
import { Accordion } from 'components/uikit/Accordion/Accordion'
import { Icon } from 'components/uikit/Icon/Icon'
import { Modal } from 'components/uikit/Modal/Modal'
import { NmaModalKeys, nmaSubmissionDetails } from 'pages/landing/nmaModalsData'

import { ReactComponent as ArrowIcon } from './icons/arrow_right.svg'
import { ReactComponent as CloseIcon } from './icons/close.svg'
import { ReactComponent as LinkIcon } from './icons/link.svg'
import styles from './NmaModal.module.scss'

interface Attachment {
  icon: 'document' | 'image' | 'video' | 'attachment' | 'pdf'
  name: string
  link: string
  type?: 'redirect' | 'replace'
}
export interface NmaModalProps {
  open: boolean
  onClose: () => void
  headerTitle: string
  coverImage: string
  nmaCategory: string
  ouName: string
  submittedBy: string
  attachments?: Attachment[]
  fields?: any
  videoUrl: string
  keyVisualUrl: string
  submissionDetailsKey?: NmaModalKeys
}
const NmaModal = ({
  open,
  onClose,
  headerTitle,
  coverImage,
  nmaCategory,
  ouName,
  submittedBy,
  attachments,
  submissionDetailsKey,
}: NmaModalProps) => {
  const [activeCoverImage, setActiveCoverImage] = useState(coverImage)
  const [coverImageObjectContain, setCoverImageObjectContain] = useState(false)

  const getIcon = (icon: Attachment['icon']) => require(`./icons/${icon}.svg`)

  const [showSubmissions, setShowSubmissions] = useState(false)

  const getButtonIcon = (icon: Attachment['icon']) => {
    switch (icon) {
      case 'document':
        return <LinkIcon />
      case 'video':
        return <ArrowIcon />
      case 'image':
        return <ArrowIcon />
      case 'attachment':
        return <LinkIcon />
      case 'pdf':
        return <LinkIcon />
      default:
        return <ArrowIcon />
    }
  }

  return (
    <Modal open={open} className={styles.NmaModal} size="l" onClose={() => setShowSubmissions(false)}>
      <div slot="header" className={styles.header}>
        <div className={styles.headerTitle}>{headerTitle}</div>
        <div
          className={styles.close}
          onClick={() => {
            setShowSubmissions(false)
            onClose()
          }}
        >
          <CloseIcon />
        </div>
      </div>
      <div slot="body" className={styles.body}>
        <div className="flex flex-col gap-4">
          <div className={styles.coverImageWrapper}>
            <img
              src={activeCoverImage}
              alt={headerTitle}
              className={clsx(styles.coverImage, { [styles.contain]: coverImageObjectContain })}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.row}>
              <div className={styles.title}>NMA CATEGORY</div>
              <div className={styles.value}>{nmaCategory}</div>
            </div>
            <div className={styles.row}>
              <div className={styles.title}>OU NAME</div>
              <div className={styles.value}>{ouName}</div>
            </div>
            <div className={styles.row}>
              <div className={styles.title}>SUBMITTED BY</div>
              <div className={styles.value}>{submittedBy}</div>
            </div>
          </div>
        </div>
        <div className={styles.attachments}>
          <div className={styles.submittedAttachments}>Submitted Attachments ({attachments?.length})</div>
          {!!attachments?.length &&
            attachments.map(({ icon, name, link, type }, index) => {
              const handleOnAttachmentClick = () => {
                if (type === 'replace') {
                  setActiveCoverImage(require(`../../pages/landing/${link}`))
                  setCoverImageObjectContain(true)
                } else {
                  window.open(link, '_blank')
                }
              }
              return (
                <div key={index} className={styles.attachment} onClick={handleOnAttachmentClick}>
                  <img src={getIcon(icon)} alt={name} className={styles.icon} />
                  <div className={styles.name}>{name}</div>
                  <div className={styles.linkIcon}>{getButtonIcon(icon)}</div>
                </div>
              )
            })}
        </div>
        <Accordion
          size="m"
          withDivider={false}
          expanded={showSubmissions}
          onChange={() => setShowSubmissions(prev => !prev)}
          className={clsx(styles.submissionAccordion, 'col-span-2', { [styles.open]: showSubmissions })}
        >
          <div className="flex items-center gap-1" slot="header">
            <Icon icon={ChevronDown} />
            <span className={styles.showSubmissionDetails}>
              {showSubmissions ? 'Hide Submission Details' : 'Show Submission Details'}
            </span>
          </div>
          {submissionDetailsKey && showSubmissions && (
            <div className={styles.submissionDetailsContainer}>
              <SubmissionDetails _modalKey={submissionDetailsKey} />
            </div>
          )}
        </Accordion>
      </div>

      <div slot="actions" className="px-6 pb-6 pt-2">
        <div className="flex justify-end flex-grow-1 items-end">
          <Button
            className={styles.closeCTA}
            variant="secondary"
            border
            text="Close"
            onClick={() => {
              setShowSubmissions(false)
              onClose()
            }}
          />
        </div>
      </div>
    </Modal>
  )
}

export default NmaModal

const SubmissionDetails = ({ _modalKey }: { _modalKey: NmaModalKeys }) => {
  const data = nmaSubmissionDetails.find(({ modalKey }) => modalKey === _modalKey)

  return (
    <div className={clsx(styles.contentContainer, styles.submissionContainer)}>
      <Flex direction="column" className="mb-6">
        <div className={styles.contentTitle}>Networked Marketing Awards 2024</div>
        <div className={styles.category}>{data?.awardsTitle}</div>
      </Flex>
      <div className={styles.submissionTitle}>{data?.title}</div>
      <hr className={styles.divider} />
      <Flex direction="column" gap={12} className={styles.detailsContainer}>
        <div className={styles.title2}>Entry Details</div>
        <Flex direction="column">
          <div className={styles.subtitle}>Which TCCC product category is this for?</div>
          <div className={styles.text}>{data?.productCategory}</div>
        </Flex>
        <Flex direction="column">
          <div className={styles.subtitle}>Which OU are you submitting from?</div>
          <div className={styles.text}>{data?.ouSubmission}</div>
        </Flex>
        <Flex direction="column">
          <div className={styles.subtitle}>Which OU(s) does this work live in?</div>
          <div className={styles.text}>{data?.ouWork}</div>
        </Flex>
        <Flex direction="column">
          <div className={styles.subtitle}>Key Contact</div>
          <div className={styles.text}>{data?.keyContact}</div>
        </Flex>
        <Flex direction="column">
          <div className={styles.subtitle}>When did this work go live?</div>
          <div className={styles.text}>{data?.workGoLive}</div>
        </Flex>
        <Flex direction="column">
          <div className={styles.subtitle}>Team Members</div>
          <div className={styles.text}>
            <ul>
              {data?.teamMembers?.map((name, index) => (
                <li key={index} className="list-disc ml-7">
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </Flex>

        <hr className={styles.divider} />
        <div className={styles.title2}>Strategy</div>
        <Flex direction="column">
          <div className={styles.subtitle}>What brand challenge were you trying to solve?</div>
          <div className={styles.text}>{data?.strategy.brandOrBusiness}</div>
        </Flex>

        <Flex direction="column">
          <div className={styles.subtitle}>What human tensions did you tackle?</div>
          <div className={styles.text}>{data?.strategy.humanTensions}</div>
        </Flex>

        {!!data?.strategy.keyInsight && (
          <Flex direction="column">
            <div className={styles.subtitle}>Detail the key insight your experience is built upon.</div>
            <div className={styles.text}>{data?.strategy.keyInsight}</div>
          </Flex>
        )}

        <div className={clsx(styles.title2, 'mt-[16px] mb-[8px]')}>Creative Execution</div>

        <Flex direction="column">
          <div className={styles.subtitle}>Describe your big idea.</div>
          <div className={styles.text}>{data?.creativeExecution.bigIdea}</div>
        </Flex>

        <Flex direction="column">
          <div className={styles.subtitle}>
            Does this Live Experience amplify the existing Persistent Platform, and how?
          </div>
          <div className={styles.text}>{data?.creativeExecution.amplifyProduct}</div>
        </Flex>

        <Flex direction="column">
          <div className={styles.subtitle}>How did it spring to life and make a splash in the market?</div>
          <div className={styles.text}>{data?.creativeExecution.springToLife}</div>
        </Flex>

        {!!data?.creativeExecution.externalPartnerships && (
          <Flex direction="column">
            <div className={styles.subtitle}>
              Describe any external partnerships or collaborations that were part of your experience.
            </div>
            <div className={styles.text}>{data?.creativeExecution.externalPartnerships}</div>
          </Flex>
        )}

        <div className={clsx(styles.title2, 'mt-[16px] mb-[8px]')}>Impact</div>

        <Flex direction="column">
          <div className={styles.subtitle}>
            Provide evidence of the impact your experience had on business and brand metrics.
          </div>
          <div className={styles.text}>{data?.impact.impactExperience}</div>
        </Flex>

        <Flex direction="column">
          <div className={styles.subtitle}>Discuss the reach of your experience beyond its original market.</div>
          <div className={styles.text}>{data?.impact.reachExperience}</div>
        </Flex>

        <Flex direction="column">
          <div className={styles.subtitle}>
            Illustrate how your team has embraced and demonstrated networked ways of working that reflect our
            organizational culture.
          </div>
          <div className={styles.text}>{data?.impact.illustrateTeamEmbraced}</div>
        </Flex>

        <Flex direction="column">
          <div className={styles.subtitle}>Share key learnings and insights gained from this project.</div>
          <div className={styles.text}>{data?.impact.shareKeyLearnings}</div>
        </Flex>
      </Flex>
    </div>
  )
}
